import { useNavigate } from 'react-router-dom';
import {
  ChartCard,
  DailySeriesChart,
  MetricCard,
  SubUnitStatsTable,
  formatCount,
  formatUsd,
  metricInfo,
  metricTone,
  metricIcon,
  type SubUnitColumn,
} from '@kioskgaming/ui';
import type { DashboardV2Period, ShopDashboardV2PlayersData } from '../../services/api';

type Props = {
  period: DashboardV2Period;
  data: ShopDashboardV2PlayersData | null;
  isLoading: boolean;
};

type TopPlayerRow = ShopDashboardV2PlayersData['extras']['topPlayers'][number];

const KPI_LAYOUT: Array<{ key: keyof ShopDashboardV2PlayersData['kpis']; point?: boolean }> = [
  { key: 'activePlayers' },
  { key: 'newPlayers' },
  { key: 'avgDeposit' },
  { key: 'firstDepositConversionPct' },
  { key: 'playerCreditLiability', point: true },
];

const topPlayerColumns: Array<SubUnitColumn<TopPlayerRow>> = [
  {
    key: 'username',
    header: 'Player',
    render: (row) => <span className="font-medium text-gray-900">{row.username}</span>,
  },
  {
    key: 'deposits',
    header: 'Deposits',
    tooltip: 'Total completed deposits by this Player in the period.',
    align: 'right',
    render: (row) => formatUsd(row.deposits),
  },
  {
    key: 'count',
    header: 'Deposit count',
    align: 'right',
    render: (row) => formatCount(row.count),
  },
];

export default function PlayersTab({ data, isLoading }: Props) {
  const navigate = useNavigate();
  const kpis = data?.kpis;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-5">
        {KPI_LAYOUT.map(({ key, point }) => {
          const info = metricInfo(key);
          const format =
            key === 'firstDepositConversionPct'
              ? (n: number) => `${n.toFixed(1)}%`
              : key === 'avgDeposit' || key === 'playerCreditLiability'
                ? formatUsd
                : formatCount;
          return (
            <MetricCard
              key={key}
              label={info.label}
              tooltip={info.tooltip}
              value={format(kpis?.[key]?.value ?? 0)}
              kpi={point ? null : kpis?.[key] ?? null}
              goodWhen={info.goodWhen}
              tone={metricTone(key)}
              icon={metricIcon(key)}
              loading={isLoading}
            />
          );
        })}
      </div>

      <div className="grid grid-cols-1 gap-3 xl:grid-cols-2">
        <ChartCard
          title="Active Players per day"
          tooltip="Unique Players with payment activity on each day of the period."
          loading={isLoading}
          empty={!isLoading && !!data && data.charts.dailyActivePlayers.every((d) => d.active === 0)}
        >
          <DailySeriesChart
            data={data?.charts.dailyActivePlayers ?? []}
            xKey="date"
            series={[{ key: 'active', label: 'Active Players' }]}
            valueFormatter={formatCount}
          />
        </ChartCard>
        <div>
          <h3 className="mb-3 text-sm font-semibold text-gray-800">Top Players by deposits</h3>
          <SubUnitStatsTable
            columns={topPlayerColumns}
            rows={data?.extras.topPlayers ?? []}
            rowKey={(row) => row.userId}
            loading={isLoading}
            emptyLabel="No Player deposits in this period"
            onRowClick={(row) => navigate(`/players/${row.userId}`)}
          />
        </div>
      </div>
    </div>
  );
}
